import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Body, BodyHighlight, Description, SectionTitle } from "@ddm-design-system/typography";
import { LinkButton } from "@ddm-design-system/button";
import { withAggRequest, IWithAggRequestProps } from "../../common/hoc/withAggRequest";
import { getBeverages } from "../../../store/beers/selectors";
import { getChosenOutletsX10s } from "../../../store/installation/selectors";
import { IMachineConsumption } from "../../../services/aggregator-helpers";
import { formatNumber, generateInitials } from "../../../helpers";
import useX10Text from "../../installation/x10/useX10Text";

const TOP_MACHINES = 10;

const MachineChartCard: React.FC<IWithAggRequestProps> = ({ data, loading, error }) => {
  const t = useX10Text();
  const x10s = useSelector(getChosenOutletsX10s);
  const beverages = useSelector(getBeverages);
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    const byId = new Map(x10s.map(x10 => [x10.id, x10]));
    // the split by component also returns pressure chambers: keep the X10s only
    return ((data?.machines || []) as IMachineConsumption[])
      .flatMap(machine => {
        const x10 = byId.get(machine.id);
        if (!x10) {
          return [];
        }
        const beverage = beverages.find(b => b.id === x10.beverageId);
        return [{ ...machine, x10, beverage }];
      })
      .sort((a, b) => b.value - a.value);
  }, [data, x10s, beverages]);

  const visibleRows = showAll ? rows : rows.slice(0, TOP_MACHINES);
  const maxValue = rows[0]?.value || 1;

  return (
    <div className="flex flex-col gap-md w-full" data-test-id="machine-chart">
      <div className="flex items-baseline gap-md flex-wrap">
        <SectionTitle>{t("volume_per_machine")}</SectionTitle>
        <Description className="text-grey-grey100">{t("volume_per_machine_sub")}</Description>
      </div>
      {/* the request only starts once the card scrolls into view */}
      {loading || (!data && !error) ? (
        <div className="h-[200px] rounded-10 bg-grey-grey25 animate-pulse-fade-in" />
      ) : rows.length === 0 ? (
        <Body className="text-grey-grey100">{t("volume_per_machine_empty")}</Body>
      ) : (
        <div className="flex flex-col">
          {visibleRows.map(row => {
            const change =
              row.valueBefore !== undefined && row.valueBefore > 0
                ? Math.round(((row.value - row.valueBefore) / row.valueBefore) * 100)
                : undefined;
            return (
              <div
                key={row.id}
                className="flex items-center gap-md flex-wrap py-xs border-t border-grey-grey25"
                data-test-id="machine-chart-row"
              >
                <div
                  className="flex items-center gap-sm min-w-[180px]"
                  // Tailwind 2 doesn't turn "_" into spaces inside arbitrary values
                  style={{ flex: "0 1 260px" }}
                >
                  {row.beverage?.logoUrl ? (
                    <img
                      src={row.beverage.logoUrl}
                      alt=""
                      className="w-[32px] h-[32px] rounded-[50%] flex-shrink-0"
                    />
                  ) : (
                    <span
                      className="flex items-center justify-center w-[32px] h-[32px] rounded-[50%] flex-shrink-0 bg-grey-grey50 text-white"
                      style={{ backgroundColor: row.beverage?.mainColor || undefined }}
                    >
                      <Description>
                        {row.beverage
                          ? generateInitials(row.beverage.brand, row.beverage.subbrand)
                          : "?"}
                      </Description>
                    </span>
                  )}
                  <div className="flex flex-col min-w-0 text-left">
                    <BodyHighlight className="truncate">{row.x10.name}</BodyHighlight>
                    <Description className="truncate text-grey-grey100">
                      {row.x10.parentName}
                      {row.beverage ? ` · ${row.beverage.name}` : ""}
                    </Description>
                  </div>
                </div>
                <div
                  className="flex items-center gap-md min-w-[160px]"
                  style={{ flex: "1 1 280px" }}
                >
                  <div className="relative flex-1 h-[20px] rounded-[4px] bg-grey-grey25">
                    <div
                      className="absolute left-0 top-0 bottom-0 rounded-[4px] bg-black"
                      style={{ width: `${Math.round((row.value / maxValue) * 100)}%` }}
                    />
                  </div>
                  <BodyHighlight className="w-[64px] text-right">
                    {formatNumber(row.value, "L", false)}
                  </BodyHighlight>
                  <Description
                    className={`w-[48px] text-right ${
                      change === undefined
                        ? "text-grey-grey100"
                        : change >= 0
                        ? "text-success-success100"
                        : "text-alert-alert100"
                    }`}
                  >
                    {change === undefined ? "—" : `${change > 0 ? "+" : ""}${change}%`}
                  </Description>
                </div>
              </div>
            );
          })}
          {rows.length > TOP_MACHINES && (
            <div className="flex justify-center pt-md">
              <LinkButton onClick={() => setShowAll(!showAll)}>
                {showAll ? t("show_less") : t("show_all", { COUNT: rows.length })}
              </LinkButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default withAggRequest(MachineChartCard);
